<template>
  <div>
    <section ref="target">
      <h2 class="bg-white pt-12 text-center text-xl font-normal md:bg-beige1 md:pt-16 md:text-2xl">Location</h2>
      <div class="bg-white pb-7 pt-2 md:bg-beige1 md:pb-14"><div class="mx-auto w-12 border-b border-gold1 md:w-14" /></div>
      <div class="bg-white md:mx-[5%] md:py-10 e2:m-auto e2:max-w-6xl">
        <div :class="['relative mx-auto md:w-[640px]', destinationImageUrl ? 'min-h-[420px] pb-80' : '']">
          <div class="'z-10 md:mx-0' mx-4 flex flex-row items-start md:relative">
            <img class="justify-start" height="24" width="24" :src="locationIconUrl" />
            <div class="relative z-20 flex w-[90%] flex-col items-start justify-center pl-2 md:w-1/2">
              <p class="mb:mb-6 mb-0 flex w-full flex-col break-words md:m-0 md:text-[17px]">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="convertNewlinesToBreaks(props.experience.venueOverview)"></span>
              </p>
              <div v-if="props.experience.venueDetail.length > 0 || googleMapsUrl" class="mr-auto mt-2 flex gap-x-4">
                <a v-if="props.experience.venueDetail.length > 0 || googleMapsUrl" class="link cursor-pointer text-gold1 underline underline-offset-2" @click="updateModalOpen(true)"
                  >Location Details</a
                >
                <a
                  v-if="googleMapsUrl"
                  aria-label="Open in Google Maps (opens in a new tab)"
                  class="link text-gold1 underline underline-offset-2"
                  rel="noopener noreferrer"
                  target="_blank"
                  :href="googleMapsUrl"
                  >Google Maps</a
                >
              </div>
            </div>
          </div>
          <OptimizedImage v-if="destinationImageUrl" :class="['absolute bottom-0 right-0 z-0 h-[400px]']" :image-url="destinationImageUrl" :optimized-image-options="{ width: 720 }" />
        </div>
      </div>
      <MoleculesWabunkaResponsiveDrawer :external-open-state="isModalOpen" :on-click="() => {}" @update:is-modal-open="updateModalOpen">
        <LocationMapDetail
          v-if="googleMapsUrl"
          :google-maps-url="googleMapsUrl"
          :is-loading="isStationsLoading"
          :is-map-loading="isMapLoading"
          :origin="origin"
          :static-map-url="staticMapUrl"
          :stations="stations"
          :venue-detail="experience.venueDetail"
        />
        <!-- 座標が無くマップを出せない場合のみ、開催地詳細だけを単体で表示する（マップ表示時は上の Map Detail 内に統合）。 -->
        <OrganismsPagesEnExperiencesDetailLocationSectionLocationDetail v-else-if="experience.venueDetail.length > 0" :venue-detail="experience.venueDetail" />
      </MoleculesWabunkaResponsiveDrawer>
    </section>
  </div>
</template>

<script lang="ts" setup>
import locationIconUrl from '@/assets/icon/location/default.svg';
import type { WabunkaExperienceViewModel } from '@/models/wabunka/wabunkaExperience';
import { convertNewlinesToBreaks } from '@/utils/stringUtils';
import { OptimizedImage } from '@/wabunka-src/common/components';
import { sendGAWabunkaLocationDetailsOpenEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/locationDetailsOpen';
import { buildGoogleMapsSearchUrl } from '@/wabunka-src/common/utils/googleMaps';
import { useNearestStationsQuery } from '@/wabunka-src/features/Experience/composables/query/useNearestStationsQuery';
import { useStaticMapUrlQuery } from '@/wabunka-src/features/Experience/composables/query/useStaticMapUrlQuery';
import LocationMapDetail from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/pure/LocationMapDetail/LocationMapDetail.vue';

interface Props {
  experience: WabunkaExperienceViewModel;
}

const props = defineProps<Props>();

const shouldShowMap = ref(false);
const target = ref<HTMLElement | null>(null);
const isModalOpen = ref(false);

// Static マップ・最寄り駅は「Location Details」開封時にだけ遅延ロードする（直帰の多い詳細PV全件に課金しない）。
const experienceId = computed(() => props.experience.id);
const { stations, status: stationsStatus, execute: executeStations } = useNearestStationsQuery(experienceId);
const { staticMapUrl, status: staticMapStatus, execute: executeStaticMap } = useStaticMapUrlQuery(experienceId);
const isStationsLoading = computed(() => stationsStatus.value === 'pending');
const isMapLoading = computed(() => staticMapStatus.value === 'pending');
const origin = computed(() => ({ latitude: props.experience.latitude ?? 0, longitude: props.experience.longitude ?? 0 }));

// 体験の緯度経度から Google Maps 検索 URL を生成する。座標が無い場合は null（リンク・マップ非表示）。
const googleMapsUrl = computed(() => buildGoogleMapsSearchUrl(props.experience.latitude, props.experience.longitude));

const updateModalOpen = (newVal: boolean) => {
  isModalOpen.value = newVal;
  // 開封時のみ計測＋遅延ロード（座標がある体験のみ）。
  // status は同一ページの Overview セクション側と useFetch の key（path）経由で共有されるため、
  // idle（どちらのセクションでも未取得）の場合だけ実行し、
  // 「開封数 ÷ 詳細PV ＝ Static 実課金数」の計測前提を崩さない（二重課金・二重計測防止）。
  // GA 発火も座標あり時に限定する（座標なしは Static 課金リクエストが飛ばないため）。
  if (newVal && experienceId.value && googleMapsUrl.value && staticMapStatus.value === 'idle') {
    sendGAWabunkaLocationDetailsOpenEvent({ experienceId: experienceId.value });
    void executeStations();
    void executeStaticMap();
  }
};
onMounted(async () => {
  await nextTick(); // DOMが完全に描画されるまで待つ

  const observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) {
      shouldShowMap.value = true;
    }
  });

  if (target.value) {
    observer.observe(target.value);
  } else {
    console.error('Target element is not available');
  }
});

// 最適化は OptimizedImage 側で行うため、ここでは生の imgPath（無ければ null）を返す。
const destinationImageUrl = computed(() => {
  const destinationTag = props.experience.tags.find((experienceTag) => experienceTag.tag.tagGroupType === 'destination');
  return destinationTag && destinationTag.tag.imgPath.length > 0 ? destinationTag.tag.imgPath : null;
});
</script>
